import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { adminService } from '../../../services/adminService';

const SystemOverview = ({ stats, onRefresh }) => {
  const [systemHealth, setSystemHealth] = useState({
    database: 'healthy',
    storage: 'healthy',
    auth: 'healthy',
    lastCheck: new Date()?.toISOString()
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);

  useEffect(() => {
    loadRecentActivity();
    checkSystemHealth();
  }, []);

  const loadRecentActivity = async () => {
    try {
      setLoadingActivity(true);
      const result = await adminService?.getUserActivitySummary();
      if (result?.data) {
        // Format the activities for display
        const formattedActivities = result?.data?.slice(0, 5)?.map(activity => ({
          id: activity?.id,
          type: activity?.activity_type,
          message: `${activity?.user_profiles?.full_name} performed ${activity?.activity_type} action`,
          timestamp: activity?.created_at,
          icon: getActivityIcon(activity?.activity_type),
          iconColor: getActivityIconColor(activity?.activity_type)
        }));
        setRecentActivity(formattedActivities);
      }
    } catch (error) {
      console.error('Error loading recent activity:', error);
      setRecentActivity([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const checkSystemHealth = async () => {
    try {
      // Test database connection by trying to fetch system stats
      const systemResult = await adminService?.getSystemStats();
      const dbHealthy = systemResult?.data?.totalUsers !== undefined;
      
      setSystemHealth({
        database: dbHealthy ? 'healthy' : 'warning',
        storage: 'healthy', // Assume healthy for now
        auth: 'healthy', // If we got here, auth is working
        lastCheck: new Date()?.toISOString()
      });
    } catch (error) {
      console.error('Error checking system health:', error);
      setSystemHealth(prev => ({
        ...prev,
        database: 'error',
        lastCheck: new Date()?.toISOString()
      }));
    }
  };

  const getActivityIcon = (activityType) => {
    const icons = {
      added: 'Plus',
      valued: 'DollarSign',
      sold: 'TrendingDown',
      purchased: 'ShoppingCart',
      updated: 'Edit',
      market_update: 'TrendingUp'
    };
    return icons?.[activityType] || 'Activity';
  };

  const getActivityIconColor = (activityType) => {
    const colors = {
      added: 'text-green-600',
      valued: 'text-blue-600',
      sold: 'text-red-600',
      purchased: 'text-purple-600',
      updated: 'text-yellow-600',
      market_update: 'text-indigo-600'
    };
    return colors?.[activityType] || 'text-gray-600';
  };

  const handleRefresh = () => {
    loadRecentActivity();
    checkSystemHealth();
    onRefresh();
  };

  const healthStatus = {
    healthy: { color: 'text-success', bg: 'bg-success/10', label: 'Healthy' },
    warning: { color: 'text-warning', bg: 'bg-warning/10', label: 'Warning' },
    error: { color: 'text-destructive', bg: 'bg-destructive/10', label: 'Error' }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  return (
    <div className="space-y-8">
      {/* System Health */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">System Health</h3>
          <button
            onClick={handleRefresh}
            className="flex items-center space-x-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-md hover:bg-muted/50 transition-colors"
          >
            <Icon name="RefreshCw" size={14} />
            <span>Refresh</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${healthStatus?.[systemHealth?.database]?.bg}`}>
                <Icon name="Database" className={healthStatus?.[systemHealth?.database]?.color} size={20} />
              </div>
              <div>
                <p className="font-medium text-foreground">Database</p>
                <p className="text-sm text-muted-foreground">Supabase Connection</p>
              </div>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${healthStatus?.[systemHealth?.database]?.bg} ${healthStatus?.[systemHealth?.database]?.color}`}>
              {healthStatus?.[systemHealth?.database]?.label}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${healthStatus?.[systemHealth?.storage]?.bg}`}>
                <Icon name="HardDrive" className={healthStatus?.[systemHealth?.storage]?.color} size={20} />
              </div>
              <div>
                <p className="font-medium text-foreground">Storage</p>
                <p className="text-sm text-muted-foreground">File System</p>
              </div>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${healthStatus?.[systemHealth?.storage]?.bg} ${healthStatus?.[systemHealth?.storage]?.color}`}>
              {healthStatus?.[systemHealth?.storage]?.label}
            </span>
          </div>

          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${healthStatus?.[systemHealth?.auth]?.bg}`}>
                <Icon name="Key" className={healthStatus?.[systemHealth?.auth]?.color} size={20} />
              </div>
              <div>
                <p className="font-medium text-foreground">Authentication</p>
                <p className="text-sm text-muted-foreground">User Sessions</p>
              </div>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${healthStatus?.[systemHealth?.auth]?.bg} ${healthStatus?.[systemHealth?.auth]?.color}`}>
              {healthStatus?.[systemHealth?.auth]?.label}
            </span>
          </div>
        </div>

        <div className="mt-4 text-xs text-muted-foreground">
          Last checked: {formatTimeAgo(systemHealth?.lastCheck)}
        </div>
      </div>
      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Recent Activity</h3>
        
        <div className="space-y-4">
          {loadingActivity ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
              <p className="text-muted-foreground mt-2">Loading activity...</p>
            </div>
          ) : recentActivity?.length > 0 ? (
            recentActivity?.map((activity) => (
              <div key={activity?.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                <div className={`p-1.5 rounded-lg bg-muted`}>
                  <Icon name={activity?.icon} className={activity?.iconColor} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {activity?.message}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatTimeAgo(activity?.timestamp)}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Icon name="Activity" className="text-muted-foreground mx-auto mb-2" size={24} />
              <p className="text-muted-foreground">No recent activity</p>
            </div>
          )}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Quick Actions</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors text-left">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="UserPlus" className="text-primary" size={20} />
            </div>
            <div>
              <p className="font-medium text-foreground">Add User</p>
              <p className="text-xs text-muted-foreground">Create new account</p>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors text-left">
            <div className="p-2 bg-success/10 rounded-lg">
              <Icon name="Image" className="text-success" size={20} />
            </div>
            <div>
              <p className="font-medium text-foreground">Update Featured</p>
              <p className="text-xs text-muted-foreground">Change homepage photos</p>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors text-left">
            <div className="p-2 bg-warning/10 rounded-lg">
              <Icon name="Clock" className="text-warning" size={20} />
            </div>
            <div>
              <p className="font-medium text-foreground">Coming Soon</p>
              <p className="text-xs text-muted-foreground">Toggle site access</p>
            </div>
          </button>

          <button className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors text-left">
            <div className="p-2 bg-accent/10 rounded-lg">
              <Icon name="Download" className="text-accent" size={20} />
            </div>
            <div>
              <p className="font-medium text-foreground">Export Data</p>
              <p className="text-xs text-muted-foreground">Download reports</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemOverview;